import React, { Fragment, Suspense, lazy, useState, useEffect } from "react";
import { ThemeProvider, StyledEngineProvider, CssBaseline, Alert } from "@mui/material";
import { BrowserRouter, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { createAppTheme } from "./theme";
import GlobalStyles from "./GlobalStyles";
import Pace from "./shared/components/Pace";
import { useAuth } from "./hooks/useAuth";
import { AuthWrapper } from './components/AuthWrapper';
import { RTLProvider, useRTL } from './contexts/RTLContext';
import { TranslationProvider, useTranslation } from './contexts/TranslationContext';
import { initStripe } from "./utils/stripe";
import { LanguageProvider } from './contexts/LanguageContext';
import MobileAuthHandler from './components/MobileAuthHandler';
import UserGuide from './shared/components/UserGuide';
import TermsOfService from './logged_out/components/register_login/TermsOfService';
import PrivacyPolicy from './logged_out/components/register_login/PrivacyPolicy';

const LoggedInComponent = lazy(() => import("./logged_in/components/Main"));
const LoggedOutComponent = lazy(() => import("./logged_out/components/Main"));
const SubscriptionSuccess = lazy(() => import("./logged_in/components/subscription/SubscriptionSuccess"));

function AppRoutes() {
  const { isAuthenticated, isLoading } = useAuth();
  const [sessionExpired, setSessionExpired] = useState(false);
  const { isRTL } = useRTL();
  const { getString } = useTranslation();
  const theme = React.useMemo(() => createAppTheme(isRTL), [isRTL]);
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('App: Current URL:', window.location.href);
    console.log('App: URL search params:', window.location.search);
    
    // Check for mobile app token and store it before any cleanup happens
    const mobileToken = urlParams.get('token');
    if (mobileToken) {
      console.log('App: Mobile token detected:', mobileToken);
      sessionStorage.setItem('isMobileApp', 'true');
      sessionStorage.setItem('mobileTokenDetected', Date.now().toString());
    }
    
    // Check for page parameter (works with or without token)
    const targetPage = urlParams.get('page');
    if (targetPage) {
      console.log('App: Target page detected (raw):', targetPage);
      
      // Clean the page parameter - remove extra text and decode URL
      let cleanPage = decodeURIComponent(targetPage);
      console.log('App: Target page after decode:', cleanPage);
      
      // Extract just the path part before any extra text
      const pathMatch = cleanPage.match(/^(\/c\/[a-zA-Z0-9\-_]+)/);
      if (pathMatch) {
        cleanPage = pathMatch[1];
      } else {
        // If it doesn't start with /c/, try to extract just the page name
        const pageMatch = cleanPage.match(/^([a-zA-Z0-9\-_]+)/);
        if (pageMatch) {
          cleanPage = pageMatch[1];
        }
      }
      
      console.log('App: Target page after cleaning:', cleanPage);
      
      // Handle both formats: "import-spreadsheet" and "/c/import-spreadsheet"
      let destination;
      if (cleanPage.startsWith('/c/')) {
        destination = cleanPage;
      } else {
        destination = `/c/${cleanPage}`;
      }
      console.log('App: Setting intended destination to:', destination);
      sessionStorage.setItem('intendedDestination', destination);
    } else {
      // Check if there's a redirect parameter (common in mobile apps)
      const redirectTo = urlParams.get('redirect');
      if (redirectTo) {
        console.log('App: Redirect parameter detected:', redirectTo);
        sessionStorage.setItem('intendedDestination', redirectTo);
      }
    }
    
    // Store the intended destination for post-authentication redirect
    const currentPath = location.pathname;
    if (currentPath.startsWith('/c/') && currentPath !== '/c') {
      console.log('App: Storing intended destination from current path:', currentPath + location.search);
      sessionStorage.setItem('intendedDestination', currentPath + location.search);
    }
    
    // Check if redirected due to session expiration
    if (urlParams.get('session') === 'expired') {
      setSessionExpired(true);
      // Clean up URL
      window.history.replaceState({}, document.title, '/');
    }
  }, [location.pathname, location.search]);

  if (isLoading) {
    return <Pace color={theme.palette.primary.light} />;
  }

  // Helper function to preserve query parameters in redirects
  const preserveQueryParams = (targetPath) => {
    return location.search ? `${targetPath}${location.search}` : targetPath;
  };

  // Helper function to get the intended destination after authentication
  const getIntendedDestination = () => {
    const intended = sessionStorage.getItem('intendedDestination');
    console.log('App: Getting intended destination:', intended);
    console.log('App: All sessionStorage keys:', Object.keys(sessionStorage));
    if (intended) {
      sessionStorage.removeItem('intendedDestination');
      console.log('App: Redirecting to intended destination:', intended);
      return intended;
    }
    console.log('App: No intended destination, defaulting to /c');
    return '/c';
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        {sessionExpired && (
          <Alert 
            severity="warning" 
            onClose={() => setSessionExpired(false)}
            sx={{ position: 'fixed', top: 16, right: 16, zIndex: 2000 }}
          >
            {getString('SESSION_EXPIRED')}
          </Alert>
        )}
        <Suspense fallback={<Pace color={theme.palette.primary.light} />}>
          <MobileAuthHandler />
          <Switch>
            <Route path="/success" component={SubscriptionSuccess} />
            <Route path="/user-guide">
              {/* User guide accessible to everyone */}
              <UserGuide />
            </Route>
            <Route path="/terms">
              {/* Terms accessible to everyone */}
              <TermsOfService />
            </Route>
            <Route path="/privacy">
              {/* Privacy policy accessible to everyone */}
              <PrivacyPolicy />
            </Route>
            <Route path="/c">
              {isAuthenticated ? (
                <LoggedInComponent />
              ) : (
                <Redirect to={preserveQueryParams("/")} />
              )}
            </Route>
            <Route path="/">
              {isAuthenticated ? (
                <Redirect to={getIntendedDestination()} />
              ) : (
                <LoggedOutComponent />
              )}
            </Route>
          </Switch>
        </Suspense>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

function App() {
  useEffect(() => {
    // Initialize Stripe with your publishable key
    initStripe('pk_live_x65mMJxIjMAQqeEWNQ7dTxCj');
  }, []);

  return (
    <RTLProvider>
      <TranslationProvider>
        <LanguageProvider>
          <AuthWrapper>
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </AuthWrapper>
        </LanguageProvider>
      </TranslationProvider>
    </RTLProvider>
  );
}

export default App;
